import React, { useState, useEffect } from 'react';
import { GraduationCap, BookOpen, Target, Lightbulb, Users, Award } from 'lucide-react';
import PrimaryGradeContent from './PrimaryGradeContent';
import ConnectionStatusIndicator from './ConnectionStatusIndicator';
import { NotificationManager, showNotification } from './Notification';
import supabaseConnectionStatus from '../services/supabaseConnectionStatus';

const PrimaryChinese = () => {
  const [activeGrade, setActiveGrade] = useState('p1');

  // Monitor Supabase connection status
  useEffect(() => {
    let previousStatus = null;
    
    const unsubscribe = supabaseConnectionStatus.subscribe((status) => {
      // Show notification when status changes
      if (previousStatus !== null && previousStatus.status !== status.status) {
        if (status.status === 'connected') {
          showNotification('已成功連接到 Supabase', 'success', 3000);
        } else if (status.status === 'disconnected') {
          showNotification('與 Supabase 失去連接，正在嘗試重新連接...', 'error', 5000);
        }
      }
      previousStatus = status;
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const grades = [
    { id: 'p1', label: '小學一年級', icon: GraduationCap },
    { id: 'p2', label: '小學二年級', icon: BookOpen },
    { id: 'p3', label: '小學三年級', icon: Target },
    { id: 'p4', label: '小學四年級', icon: Lightbulb },
    { id: 'p5', label: '小學五年級', icon: Users },
    { id: 'p6', label: '小學六年級', icon: Award }
  ];

  return (
    <div className="space-y-6">
      {/* Notification Manager */}
      <NotificationManager />
      
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">小學中文學習</h1>
              <p className="text-gray-600">系統化的中文學習資源，涵蓋小學一至六年級</p>
            </div>
          </div>
          <ConnectionStatusIndicator />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-blue-900">閱讀理解</h3>
            </div>
            <p className="text-sm text-blue-700">多種篇章類型的閱讀理解練習</p>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Target className="w-5 h-5 text-green-600" />
              <h3 className="font-semibold text-green-900">語文運用</h3>
            </div>
            <p className="text-sm text-green-700">語法、詞彙、句式等語文知識</p>
          </div>
          
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-4 rounded-lg">
            <div className="flex items-center space-x-2 mb-2">
              <Lightbulb className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">練習題目</h3>
            </div>
            <p className="text-sm text-purple-700">豐富的練習題目與詳細解答</p>
          </div>
        </div>
      </div>

      {/* Grade Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 p-4">
            {grades.map(grade => {
              const Icon = grade.icon;
              return (
                <button
                  key={grade.id}
                  onClick={() => setActiveGrade(grade.id)}
                  className={`flex items-center space-x-2 py-3 px-4 rounded-lg font-medium text-sm transition-colors ${
                    activeGrade === grade.id
                      ? 'bg-blue-100 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{grade.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Grade Content */}
        <div className="p-6">
          <PrimaryGradeContent grade={activeGrade} />
        </div>
      </div>
    </div>
  );
};

export default PrimaryChinese; 